import { env } from '@/config/env';
import {
  RfqFilterState,
  RfqMetricsSummary,
  RfqRecord,
  RfqStatusKey,
  RfqSupplierInvitationStatus,
} from '@/types/domain/rfq-management';
import { MOCK_RFQS as CENTRAL_RFQS } from '@/mocks/database';
import { processQuery, getEntityById } from './pagination';
import { notifyGlobal } from '@/context/NotificationContext';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const GENERATED_RFQ_RECORDS: RfqRecord[] = CENTRAL_RFQS.map((rfq, i) => ({
  id: i === 0 ? 'RFQ-2001' : i === 1 ? 'RFQ-2002' : rfq.id,
  rfqNumber: i === 0 ? 'RFQ-2001' : i === 1 ? 'RFQ-2002' : rfq.rfqNumber,
  title: rfq.title,
  description: 'Enterprise Request for Quotation for hardware & materials.',
  category: rfq.category,
  sourceRequestId: rfq.requestNumber || `req-${1000 + i}`,
  sourcePRId: i === 0 ? 'PR-10001' : `pr-${1000 + i}`,
  sapPrNumber: i === 0 ? '45000123' : `45000${100 + i}`,
  procurementOwner: 'David Sterling',
  departmentName: 'Engineering',
  companyCode: '1000',
  plantCode: 'PLANT-1000',
  purchasingOrg: '1000',
  purchasingGroup: 'PGRP-001',
  priority: 'MEDIUM',
  status: (i === 0 ? 'RESPONSES_RECEIVED' : i % 5 === 0 ? 'DRAFT' : i % 3 === 0 ? 'EVALUATION_READY' : 'OPEN') as RfqStatusKey,
  estimatedTotalAmount: 50000,
  currency: 'USD',
  issueDate: rfq.createdAt,
  closingDate: rfq.submissionDeadline || '2026-10-30T17:00:00Z',
  timezone: 'UTC',
  items: rfq.items.map((it, idx) => ({
    id: it.id,
    itemNumber: (idx + 1) * 10,
    materialCode: it.materialCode,
    description: it.materialName,
    category: rfq.category,
    quantity: it.quantity,
    uom: it.unitOfMeasure,
    estimatedUnitPrice: it.targetUnitPrice || 5000,
    totalEstimatedPrice: (it.targetUnitPrice || 5000) * it.quantity,
    currency: 'USD',
    plantCode: 'PLANT-1000',
    deliveryDate: '2026-10-30',
  })),
  suppliers: (rfq.invitedVendorIds || ['vend-1']).map((vId, idx) => ({
    id: `SUP-${i + 1}-${idx + 1}`,
    supplierId: vId,
    supplierName: rfq.invitedVendorNames?.[idx] || 'Apex Hardware Solutions',
    category: rfq.category,
    contactEmail: 'sales@vendor.enterprise',
    invitationStatus: (i === 1 && idx === 0 ? 'FAILED' : 'RESPONDED') as RfqSupplierInvitationStatus,
    invitedAt: rfq.createdAt,
    respondedAt: rfq.createdAt,
    quotationId: `quo-${1000 + i}`,
    quotedAmount: 50000,
    currency: 'USD',
  })),
  commercialTerms: {
    paymentTerms: 'Net 30',
    incoterms: 'DDP',
    deliveryTerms: 'Ground',
    warrantyPeriod: '36 Months',
    validityPeriod: '60 Days',
    freightTerms: 'Prepaid',
  },
  lifecycle: [
    { key: 'OPEN', label: 'RFQ Published', status: 'COMPLETED', timestamp: rfq.createdAt },
  ],
  quotationIds: [`quo-${1000 + i}`],
  createdAt: rfq.createdAt,
  updatedAt: rfq.createdAt,
  version: 1,
}));

export class MockRfqManagementService {
  private records: RfqRecord[] = [...GENERATED_RFQ_RECORDS];

  async getRfqMetrics(): Promise<RfqMetricsSummary> {
    await delay(env.mockDelayMs);
    const totalRfqs = this.records.length;
    const drafts = this.records.filter((r) => r.status === 'DRAFT').length;
    const openRfqs = this.records.filter((r) => r.status === 'OPEN').length;
    const awaitingResponses = this.records.filter((r) => r.status === 'INVITATIONS_SENT' || r.status === 'PARTIALLY_RESPONDED').length;
    const responsesReceived = this.records.filter((r) => r.status === 'RESPONSES_RECEIVED').length;
    const deadlineApproaching = this.records.filter((r) => r.status === 'DEADLINE_APPROACHING').length;
    const evaluationReady = this.records.filter((r) => r.status === 'EVALUATION_READY').length;
    const closed = this.records.filter((r) => r.status === 'CLOSED').length;

    return {
      totalRfqs,
      drafts,
      openRfqs,
      awaitingResponses,
      responsesReceived,
      deadlineApproaching,
      evaluationReady,
      closed,
    };
  }

  async getRfqRecords(filters?: Partial<RfqFilterState>): Promise<{ items: RfqRecord[]; total: number }> {
    await delay(env.mockDelayMs);
    const queryResult = processQuery(this.records, {
      page: filters?.page || 1,
      pageSize: filters?.pageSize || 25,
      search: filters?.search,
      filters: {
        status: filters?.status,
        category: filters?.category,
        priority: filters?.priority,
        department: filters?.department,
        plant: filters?.plant,
      },
    });

    return { items: queryResult.data, total: queryResult.total };
  }

  async getRfqs(filters?: any): Promise<{ items: RfqRecord[]; total: number }> {
    return this.getRfqRecords(filters);
  }

  async getRfqById(id: string): Promise<RfqRecord | null> {
    await delay(env.mockDelayMs);
    const found = getEntityById(this.records, id, 'id') || getEntityById(this.records, id, 'rfqNumber');
    if (found) return found;
    if (this.records.length > 0) {
      const fallback = Object.assign({}, this.records[0], { id, rfqNumber: id }) as RfqRecord;
      this.records.unshift(fallback);
      return fallback;
    }
    return null;
  }

  async createRfq(payload: Partial<RfqRecord>): Promise<RfqRecord> {
    await delay(env.mockDelayMs);
    const seq = Math.floor(1000 + Math.random() * 9000);
    const rfqNumber = `RFQ-${seq}`;

    const newRecord: RfqRecord = {
      id: rfqNumber,
      rfqNumber,
      title: payload.title || 'New Enterprise RFQ',
      description: payload.description || 'Request for Quotation for IT & Industrial Materials',
      category: payload.category || 'IT Hardware',
      sourceRequestId: payload.sourceRequestId || `req-${seq}`,
      sourcePRId: payload.sourcePRId || `pr-${seq}`,
      procurementOwner: payload.procurementOwner || 'David Sterling',
      departmentName: payload.departmentName || 'Engineering',
      companyCode: payload.companyCode || '1000',
      plantCode: payload.plantCode || 'PLANT-1000',
      purchasingOrg: payload.purchasingOrg || '1000',
      purchasingGroup: payload.purchasingGroup || 'PGRP-001',
      priority: payload.priority || 'MEDIUM',
      status: 'OPEN',
      estimatedTotalAmount: payload.estimatedTotalAmount || 50000,
      currency: payload.currency || 'USD',
      issueDate: new Date().toISOString(),
      closingDate: payload.closingDate || '2026-10-30T17:00:00Z',
      timezone: 'UTC',
      items: payload.items || [],
      suppliers: payload.suppliers || [],
      commercialTerms: payload.commercialTerms || {
        paymentTerms: 'Net 30',
        incoterms: 'DDP',
        deliveryTerms: 'Ground',
        warrantyPeriod: '36 Months',
        validityPeriod: '60 Days',
        freightTerms: 'Prepaid',
      },
      lifecycle: [
        { key: 'OPEN', label: 'RFQ Created', status: 'COMPLETED', timestamp: new Date().toISOString() },
      ],
      quotationIds: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      version: 1,
    };

    this.records.unshift(newRecord);
    notifyGlobal('RFQ Published', `RFQ ${newRecord.rfqNumber} ("${newRecord.title}") published to vendors.`, 'StatusChange', newRecord.id);
    return newRecord;
  }

  async sendInvitations(rfqId: string, supplierList: any[]): Promise<RfqRecord> {
    await delay(env.mockDelayMs);
    const rfq = await this.getRfqById(rfqId);
    if (!rfq) throw new Error('RFQ not found');

    const suppliers = supplierList.map((sup, i) => ({
      id: `inv-${rfqId}-${i + 1}`,
      supplierId: sup.supplierId || `VND-${i + 1}`,
      supplierName: sup.supplierName || `Supplier ${i + 1}`,
      contactEmail: sup.contactEmail || `bids@supplier${i + 1}.com`,
      category: sup.category || 'General',
      invitationStatus: (i === supplierList.length - 1 && supplierList.length > 2 ? 'FAILED' : 'INVITED') as any,
      invitedAt: new Date().toISOString(),
    }));

    rfq.suppliers = suppliers as any;
    rfq.status = 'PARTIALLY_RESPONDED' as any;
    rfq.updatedAt = new Date().toISOString();

    notifyGlobal('RFQ Invitations Transmitted', `Invited ${suppliers.length} suppliers to RFQ ${rfqId}.`, 'System');
    return rfq;
  }

  async retryFailedInvitations(rfqId: string): Promise<RfqRecord> {
    await delay(env.mockDelayMs);
    const rfq = await this.getRfqById(rfqId);
    if (!rfq) throw new Error('RFQ not found');

    rfq.suppliers.forEach((s) => {
      if (s.invitationStatus === 'FAILED') {
        s.invitationStatus = 'INVITED';
      }
    });
    rfq.status = 'INVITATIONS_SENT' as any;
    rfq.updatedAt = new Date().toISOString();

    notifyGlobal('Invitations Retried', `Retried failed supplier invitations for RFQ ${rfqId}.`, 'System');
    return rfq;
  }

  async extendDeadline(rfqId: string, newDeadline: string, reason?: string): Promise<RfqRecord> {
    await delay(env.mockDelayMs);
    const rfq = await this.getRfqById(rfqId);
    if (!rfq) throw new Error('RFQ not found');

    rfq.closingDate = newDeadline;
    rfq.updatedAt = new Date().toISOString();
    notifyGlobal('RFQ Deadline Extended', `RFQ ${rfq.rfqNumber} extended to ${newDeadline}. Reason: ${reason || 'Buyer update'}`, 'StatusChange', rfqId);
    return rfq;
  }

  async extendRfqDeadline(rfqId: string, newDeadline: string, reason?: string): Promise<RfqRecord> {
    return this.extendDeadline(rfqId, newDeadline, reason);
  }

  async cancelRfq(rfqId: string, _reason?: string): Promise<RfqRecord> {
    await delay(env.mockDelayMs);
    const rfq = await this.getRfqById(rfqId);
    if (!rfq) throw new Error('RFQ not found');

    rfq.status = 'CANCELLED';
    rfq.updatedAt = new Date().toISOString();
    return rfq;
  }

  async updateRfq(id: string, data: Partial<RfqRecord>): Promise<RfqRecord> {
    await delay(env.mockDelayMs);
    const index = this.records.findIndex((r) => r.id === id || r.rfqNumber === id);
    if (index === -1) throw new Error(`RFQ ${id} not found`);
    const current = this.records[index]!;
    const updated: RfqRecord = {
      ...current,
      ...data,
      version: (current.version || 1) + 1,
      updatedAt: new Date().toISOString(),
    };
    this.records[index] = updated;
    notifyGlobal('RFQ Updated', `RFQ ${updated.rfqNumber} updated successfully.`, 'System');
    return updated;
  }

  async deleteRfq(id: string): Promise<boolean> {
    await delay(env.mockDelayMs);
    const initialLen = this.records.length;
    const target = this.records.find((r) => r.id === id || r.rfqNumber === id);
    this.records = this.records.filter((r) => r.id !== id && r.rfqNumber !== id);
    if (this.records.length < initialLen) {
      notifyGlobal('RFQ Deleted', `RFQ ${target?.rfqNumber || id} removed from ledger.`, 'System');
      return true;
    }
    return false;
  }

  syncRfqRecord(rfq: RfqRecord, previousId?: string): RfqRecord {
    const idx = this.records.findIndex(
      (r) => r.id === rfq.id || (previousId && r.id === previousId) || r.rfqNumber === rfq.rfqNumber || (rfq.sourceRequestId && r.sourceRequestId === rfq.sourceRequestId)
    );
    if (idx >= 0) {
      const merged = Object.assign({}, this.records[idx], rfq);
      this.records.splice(idx, 1);
      this.records.unshift(merged);
      return merged;
    }
    this.records.unshift(rfq);
    return rfq;
  }
}

export const mockRfqManagementService = new MockRfqManagementService();
